// The purchase value (CR_20260920_Asset_Register phase B, §5.3): value, date, supplier, PO reference, warranty and a
// note — managers only. Shown on the asset page's summary strip and Details card (asset-detail-revamp-handover); for
// anyone else it is not there at all: RLS returns them nothing, and an empty field would read as "worth nothing"
// rather than "not yours to see" (A11).
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';
import { type FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssetOverview } from '../data/queries';
import { type AssetFinancials, useSaveFinancials } from '../data/register';
import { calendarDate, localeTag } from '../lib/time';

const DAY = 86_400_000;

/** "expires in 4 months" / "expires in 12 days" / "expired 11 Mar 2026" — no reminder, no alert (§5.3). */
export function warrantyText(
  until: string,
  today: string,
  language: string,
  t: (key: string, o?: Record<string, unknown>) => string,
): string {
  const days = Math.round(
    (Date.parse(`${until}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / DAY,
  );
  if (days < 0)
    return t('value.warranty_expired', { date: calendarDate(until, language) ?? until });
  const rtf = new Intl.RelativeTimeFormat(localeTag(language), { numeric: 'auto' });
  return t('value.warranty_left', {
    when: days >= 60 ? rtf.format(Math.round(days / 30.44), 'month') : rtf.format(days, 'day'),
  });
}

export function money(v: string | number | null | undefined, currency: string, language: string) {
  if (v == null || v === '') return null;
  return `${currency} ${new Intl.NumberFormat(localeTag(language), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(v))}`;
}

interface Form {
  value: string;
  currency: string;
  purchased_at: string;
  supplier_name: string;
  po_ref: string;
  warranty_until: string;
  note: string;
}

export function ValueDialog({
  asset,
  current,
  onClose,
}: {
  asset: AssetOverview;
  current: AssetFinancials | null;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const save = useSaveFinancials();
  const [form, setForm] = useState<Form>({
    value: current?.value == null ? '' : String(current.value),
    currency: current?.currency ?? 'MYR',
    purchased_at: current?.purchased_at ?? '',
    // the old free-text supplier seeds the new field once (A13); saving writes supplier_name only
    supplier_name: current?.supplier_name ?? current?.supplier ?? '',
    po_ref: current?.po_ref ?? '',
    warranty_until: current?.warranty_until ?? '',
    note: current?.note ?? '',
  });
  const set = (k: keyof Form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  const value = form.value.trim() === '' ? null : Number(form.value.replace(/,/g, ''));
  const valueOk = value === null || (Number.isFinite(value) && value >= 0);
  const currencyOk = /^[A-Za-z]{3}$/.test(form.currency.trim());
  const text = (s: string) => s.trim() || null;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valueOk || !currencyOk) return;
    save.mutate(
      {
        asset_id: asset.asset_id,
        company_id: asset.company_id,
        value,
        currency: form.currency.trim().toUpperCase(),
        purchased_at: form.purchased_at || null,
        supplier_name: text(form.supplier_name),
        po_ref: text(form.po_ref),
        warranty_until: form.warranty_until || null,
        note: text(form.note),
      },
      { onSuccess: onClose },
    );
  };
  const date = { inputLabel: { shrink: true } } as const;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <form onSubmit={submit}>
        <DialogTitle>{t('value.edit_title', { code: asset.asset_code })}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label={t('value.value')}
                value={form.value}
                onChange={set('value')}
                error={!valueOk}
                slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                fullWidth
              />
              <TextField
                label={t('value.currency')}
                value={form.currency}
                onChange={set('currency')}
                error={!currencyOk}
                sx={{ width: 120 }}
              />
            </Stack>
            <TextField
              type="date"
              label={t('value.purchased_at')}
              value={form.purchased_at}
              onChange={set('purchased_at')}
              slotProps={date}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label={t('value.supplier')}
                value={form.supplier_name}
                onChange={set('supplier_name')}
                fullWidth
              />
              <TextField
                label={t('value.po_ref')}
                value={form.po_ref}
                onChange={set('po_ref')}
                fullWidth
              />
            </Stack>
            <TextField
              type="date"
              label={t('value.warranty_until')}
              value={form.warranty_until}
              onChange={set('warranty_until')}
              slotProps={date}
            />
            <TextField
              label={t('value.note')}
              value={form.note}
              onChange={set('note')}
              multiline
              minRows={2}
            />
            {save.error ? <Alert severity="error">{(save.error as Error).message}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            type="submit"
            variant="contained"
            disabled={save.isPending || !valueOk || !currencyOk}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
